import React, { useCallback, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronRight,
  Clipboard,
  Copy,
  HardDrive,
  Home,
  LayoutGrid,
  List,
  Monitor,
  Plus,
  RotateCw,
  Scissors,
  Info,
  Search,
  SquareTerminal,
  Trash2,
} from "lucide-react";
import {
  DRIVE_C,
  HOME_FOLDERS,
  RECYCLE_BIN,
  THIS_PC,
  emptyRecycleBin,
  getNode,
  getTrail,
  pathOf,
  restoreNode,
  searchTree,
  useFsVersion,
} from "../fs";
import { useIsMobile, useWindows } from "../WindowManager";
import { NodeIcon, visualFor } from "../fileIcons";
import { useMenu } from "../components/ContextMenu";
import { backgroundMenu, itemMenu, thisPcMenu } from "../menus";
import "./explorer.css";

function UsageBar({ used, total }) {
  const pct = Math.round((used / total) * 100);
  return (
    <div className="drive-usage">
      <div className="drive-usage__track">
        <span
          className={`drive-usage__fill ${pct > 90 ? "drive-usage__fill--full" : ""}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="drive-usage__text">
        {total - used} GB free of {total} GB
      </span>
    </div>
  );
}

const SORTERS = {
  name: (a, b) => a.name.localeCompare(b.name),
  type: (a, b) => (a.kind || "").localeCompare(b.kind || ""),
  size: (a, b) => (a.size || "").localeCompare(b.size || ""),
  modified: (a, b) => (b.modified || "").localeCompare(a.modified || ""),
};

export default function ExplorerApp({ payload }) {
  const { open, apps } = useWindows();
  const mobile = useIsMobile();
  const { showMenu } = useMenu();
  const fsVersion = useFsVersion();
  const [sortBy, setSortBy] = useState("name");
  const [spin, setSpin] = useState(0);
  const [history, setHistory] = useState([payload?.folderId || THIS_PC.id]);
  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("grid");
  const [query, setQuery] = useState("");

  const currentId = history[cursor];
  const node = getNode(currentId);
  const trail = getTrail(currentId);

  const go = useCallback(
    (id) => {
      setHistory((h) => [...h.slice(0, cursor + 1), id]);
      setCursor((c) => c + 1);
      setSelected(null);
      setQuery("");
    },
    [cursor]
  );

  const results = useMemo(() => searchTree(query), [query]);
  const items = useMemo(() => {
    const list = query ? results : [...(node.children || [])];
    return query ? list : list.sort(SORTERS[sortBy] || SORTERS.name);
    // fsVersion keeps the listing live after new/delete/restore
    // eslint-disable-next-line
  }, [query, results, node, sortBy, fsVersion, spin]);
  const selectedNode = selected ? getNode(selected) : null;

  /** Windows file association: folders navigate, files launch their app. */
  const launch = (item) => {
    if (item.type === "file") {
      if (item.open?.app) open(item.open.app, { fileId: item.id, ...item.open });
      setSelected(item.id);
      return;
    }
    go(item.target || item.id);
  };

  const activate = (item) => (mobile ? launch(item) : setSelected(item.id));

  const openProperties = (nodeId) => open("properties", { nodeId });

  const menuForItem = (item) => {
    if (item.type === "drive") {
      return [
        { label: "Open", onClick: () => go(item.id) },
        { type: "separator" },
        { label: "Properties", onClick: () => openProperties(item.id) },
      ];
    }
    return itemMenu({ node: item, open, openProperties, apps });
  };

  const menuForBackground = () => {
    if (node.type === "recycle") {
      return [
        {
          label: "Empty Recycle Bin",
          icon: Trash2,
          danger: true,
          disabled: !items.length,
          onClick: emptyRecycleBin,
        },
        { type: "separator" },
        { label: "Refresh", icon: RotateCw, onClick: () => setSpin((n) => n + 1) },
      ];
    }
    if (node.id === THIS_PC.id) {
      return thisPcMenu({
        open,
        openSystemProperties: () => open("properties", { system: true }),
      });
    }
    return backgroundMenu({
      folderId: node.id,
      view: view === "details" ? "details" : "large",
      setView: (v) => setView(v === "details" ? "details" : "grid"),
      sort: sortBy,
      setSort: setSortBy,
      onRefresh: () => setSpin((n) => n + 1),
      canCreate: Boolean(node.children) && node.type === "folder",
      tail: [
        {
          label: "Open in Terminal",
          icon: SquareTerminal,
          onClick: () => open("terminal"),
        },
        {
          label: "Properties",
          icon: Info,
          onClick: () => openProperties(node.id),
        },
      ],
    });
  };

  const address = node.id === THIS_PC.id ? "This PC" : pathOf(node.id);

  const Tile = ({ item, i }) => (
    <button
      className={`xp-tile ${selected === item.id ? "xp-tile--on" : ""}`}
      style={{ "--i": i }}
      onClick={() => activate(item)}
      onDoubleClick={() => launch(item)}
      onContextMenu={(e) => {
        setSelected(item.id);
        showMenu(e, menuForItem(item));
      }}
    >
      <NodeIcon node={item} size={38} />
      <span className="xp-tile__name">{item.name}</span>
      <span className="xp-tile__meta">
        {item.type === "file" ? item.kind : `${(item.children || []).length} items`}
      </span>
    </button>
  );

  return (
    <div className="explorer">
      {/* command bar */}
      <div className="xp-toolbar">
        <button className="xp-cmd" disabled>
          <Plus size={15} /> New
        </button>
        <span className="xp-toolbar__sep" />
        <button className="xp-icon-btn" disabled aria-label="Cut">
          <Scissors size={15} />
        </button>
        <button className="xp-icon-btn" disabled aria-label="Copy">
          <Copy size={15} />
        </button>
        <button className="xp-icon-btn" disabled aria-label="Paste">
          <Clipboard size={15} />
        </button>
        <button className="xp-icon-btn" disabled aria-label="Delete">
          <Trash2 size={15} />
        </button>

        <div className="xp-toolbar__spacer" />

        <div className="xp-viewtoggle" role="group" aria-label="View">
          <button
            className={`xp-icon-btn ${view === "grid" ? "xp-icon-btn--on" : ""}`}
            onClick={() => setView("grid")}
            aria-label="Large icons"
            aria-pressed={view === "grid"}
          >
            <LayoutGrid size={15} />
          </button>
          <button
            className={`xp-icon-btn ${view === "details" ? "xp-icon-btn--on" : ""}`}
            onClick={() => setView("details")}
            aria-label="Details"
            aria-pressed={view === "details"}
          >
            <List size={15} />
          </button>
        </div>
      </div>

      {/* address bar */}
      <div className="xp-address">
        <button
          className="xp-icon-btn"
          disabled={cursor === 0}
          onClick={() => {
            setCursor((c) => Math.max(0, c - 1));
            setSelected(null);
          }}
          aria-label="Back"
        >
          <ArrowLeft size={16} />
        </button>
        <button
          className="xp-icon-btn"
          disabled={cursor >= history.length - 1}
          onClick={() => {
            setCursor((c) => Math.min(history.length - 1, c + 1));
            setSelected(null);
          }}
          aria-label="Forward"
        >
          <ArrowRight size={16} />
        </button>
        <button
          className="xp-icon-btn"
          disabled={!trail.length}
          onClick={() => trail.length && go(trail[trail.length - 1].id)}
          aria-label="Up one level"
        >
          <ArrowUp size={16} />
        </button>
        <button
          className="xp-icon-btn"
          onClick={() => setSelected(null)}
          aria-label="Refresh"
        >
          <RotateCw size={15} />
        </button>

        <div className="xp-crumbs" title={address}>
          <NodeIcon node={node} size={14} />
          {trail.map((crumb) => (
            <button key={crumb.id} className="xp-crumb" onClick={() => go(crumb.id)}>
              {crumb.type === "drive" ? crumb.letter : crumb.name}
              <ChevronRight size={12} className="xp-crumb__sep" />
            </button>
          ))}
          <span className="xp-crumb xp-crumb--current">{node.name}</span>
        </div>

        <label className="xp-search">
          <Search size={14} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${node.name}`}
            aria-label="Search"
          />
        </label>
      </div>

      <div className="xp-main">
        <nav className="xp-nav win-scroll" aria-label="Navigation pane">
          <button
            className={`xp-nav__item ${
              currentId === THIS_PC.id ? "xp-nav__item--on" : ""
            }`}
            onClick={() => go(THIS_PC.id)}
          >
            <Home size={15} style={{ color: "var(--accent)" }} />
            Home
          </button>

          <div className="xp-nav__label">This PC</div>
          {HOME_FOLDERS.map((folder) => {
            const { icon: Icon, tone } = visualFor(folder);
            return (
              <button
                key={folder.id}
                className={`xp-nav__item xp-nav__item--child ${
                  currentId === folder.id ? "xp-nav__item--on" : ""
                }`}
                onClick={() => go(folder.id)}
              >
                <Icon size={15} style={{ color: tone }} />
                {folder.name}
              </button>
            );
          })}
          <button
            className={`xp-nav__item xp-nav__item--child ${
              currentId === DRIVE_C.id ? "xp-nav__item--on" : ""
            }`}
            onClick={() => go(DRIVE_C.id)}
          >
            <HardDrive size={15} style={{ color: "#5b6b7d" }} />
            {DRIVE_C.name}
          </button>

          <div className="xp-nav__label">Network</div>
          <button
            className={`xp-nav__item ${
              currentId === RECYCLE_BIN.id ? "xp-nav__item--on" : ""
            }`}
            onClick={() => go(RECYCLE_BIN.id)}
          >
            <Trash2 size={15} style={{ color: "var(--text-3)" }} />
            Recycle Bin
          </button>
        </nav>

        <div
          className="xp-content win-scroll"
          onContextMenu={(e) => {
            if (e.target !== e.currentTarget) return;
            showMenu(e, menuForBackground());
          }}
        >
          {node.id === THIS_PC.id && !query ? (
            <>
              <h2 className="xp-group">Folders</h2>
              <div className="xp-grid">
                {THIS_PC.quickFolders.map((folder, i) => (
                  <Tile item={folder} i={i} key={folder.id} />
                ))}
              </div>

              <h2 className="xp-group">Devices and drives</h2>
              <div className="xp-drives">
                {THIS_PC.children.map((drive, i) => (
                  <button
                    key={drive.id}
                    className={`xp-drive ${selected === drive.id ? "xp-drive--on" : ""}`}
                    style={{ "--i": i }}
                    onClick={() => activate(drive)}
                    onDoubleClick={() => launch(drive)}
                    onContextMenu={(e) => showMenu(e, menuForItem(drive))}
                  >
                    <span className="xp-drive__icon">
                      <HardDrive size={30} />
                    </span>
                    <span className="xp-drive__body">
                      <span className="xp-drive__name">{drive.name}</span>
                      <UsageBar used={drive.usedGb} total={drive.totalGb} />
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : view === "grid" ? (
            <div className="xp-grid">
              {items.map((item, i) => (
                <Tile item={item} i={i} key={item.id} />
              ))}
              {!items.length && (
                <p className="xp-empty">
                  {node.type === "recycle"
                    ? "Recycle Bin is empty."
                    : "This folder is empty."}
                </p>
              )}
            </div>
          ) : (
            <table className="xp-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Date modified</th>
                  <th>Type</th>
                  <th>Size</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr
                    key={item.id}
                    style={{ "--i": i }}
                    className={selected === item.id ? "xp-row--on" : ""}
                    onClick={() => activate(item)}
                    onDoubleClick={() => launch(item)}
                    onContextMenu={(e) => {
                      setSelected(item.id);
                      showMenu(e, menuForItem(item));
                    }}
                  >
                    <td>
                      <span className="xp-row__name">
                        <NodeIcon node={item} size={17} />
                        {item.name}
                      </span>
                    </td>
                    <td>{item.modified || "—"}</td>
                    <td>{item.kind}</td>
                    <td>{item.size || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedNode && selectedNode.type === "file" && (
          <aside className="xp-details win-scroll" aria-label="Details">
            <div
              className="xp-details__hero"
              style={{ "--tone": visualFor(selectedNode).tone }}
            >
              <NodeIcon node={selectedNode} size={34} />
            </div>
            <h3 className="xp-details__title">{selectedNode.name}</h3>
            <p className="xp-details__meta">{selectedNode.kind}</p>
            {selectedNode.summary && (
              <p className="xp-details__body">{selectedNode.summary}</p>
            )}
            <dl className="xp-props">
              <div>
                <dt>Size</dt>
                <dd>{selectedNode.size || "—"}</dd>
              </div>
              <div>
                <dt>Modified</dt>
                <dd>{selectedNode.modified || "—"}</dd>
              </div>
              <div>
                <dt>Location</dt>
                <dd className="xp-props__path">{pathOf(selectedNode.id)}</dd>
              </div>
            </dl>
            {selectedNode.deletedFrom ? (
              <button
                className="win-btn win-btn--accent"
                onClick={() => restoreNode(selectedNode.id)}
              >
                Restore
              </button>
            ) : (
              selectedNode.open && (
                <button
                  className="win-btn win-btn--accent"
                  onClick={() => launch(selectedNode)}
                >
                  Open
                </button>
              )
            )}
          </aside>
        )}
      </div>

      <div className="xp-status">
        <span>{items.length} items</span>
        {selectedNode && <span>1 item selected</span>}
        <span className="xp-status__hint">
          {mobile ? "Tap to open" : "Double-click to open"}
        </span>
      </div>
    </div>
  );
}
